// src/contexts/BusinessContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';      
import { useAuth } from './AuthContext';      
import { getBusinessData, saveBusinessData, saveOffer, deleteOffer, saveMonthlyOverhead } from '../services/dataService';      
import { calculateOfferMetrics } from '../models/OfferModel';

const BusinessContext = createContext();

export const BusinessProvider = React.memo(function BusinessProvider({ children }) {      
  const { user } = useAuth();      
  const [businessData, setBusinessData] = useState(null);      
  const [loading, setLoading] = useState(true);      
  const [error, setError] = useState(null);      
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  // Add a new state to track when data is being saved
  const [isSaving, setIsSaving] = useState(false);

  // Load business data when user changes      
  useEffect(() => {        
    const loadBusinessData = async () => {        
      // If there's no authenticated user, don't try to load data        
      if (!user) {        
        setBusinessData({      
          businessName: '',      
          industry: '',
          monthlyOverhead: {
            workspace: 0,
            adminStaff: 0,
            marketing: 0,
            software: 0,
            other: 0
          },      
          offers: []      
        });      
        setLoading(false);        
        return;        
      }      
        
      setLoading(true);        
              
      try {        
        const data = await getBusinessData(user.id);        
              
        // If no data returned, initialize with empty structure      
        if (!data) {      
          setBusinessData({      
            businessName: '',      
            industry: '',
            monthlyOverhead: {
              workspace: 0,
              adminStaff: 0,
              marketing: 0,
              software: 0,
              other: 0
            },      
            offers: []      
          });      
        } else {  
          // Ensure all offers have standardized calculations  
          if (data.offers && data.offers.length > 0) {  
            data.offers = data.offers.map(offer => calculateOfferMetrics({...offer}, data));  
          }  
            
          setBusinessData(data);      
        }      
              
        setError(null);        
      } catch (err) {        
        console.error('Error loading business data:', err);        
        setError('Failed to load business data');        
        // Initialize with empty business data on error        
        setBusinessData({        
          businessName: '',      
          industry: '',
          monthlyOverhead: {
            workspace: 0,
            adminStaff: 0,
            marketing: 0,
            software: 0,
            other: 0
          },      
          offers: []      
        });        
      } finally {        
        setLoading(false);        
      }        
    };      
        
    loadBusinessData();        
  }, [user]);

  // Function to refresh business data from the database - with debounce protection
  const refreshBusinessData = useCallback(async () => {  
    if (!user || loading) {  
      console.error("Cannot refresh business data: No authenticated user or already loading");  
      return null;  
    }  
      
    setLoading(true);  
    try {  
      const data = await getBusinessData(user.id);  
        
      if (data) {  
        // Ensure all offers have standardized calculations  
        if (data.offers && data.offers.length > 0) {  
          data.offers = data.offers.map(offer => calculateOfferMetrics({...offer}, data));  
        }  
          
        setBusinessData(data);  
      }  
        
      setError(null);  
      return data; // Return the fresh data  
    } catch (err) {  
      console.error('Error refreshing business data:', err);  
      setError('Failed to refresh business data');  
      return null;  
    } finally {  
      setLoading(false);  
    }  
  }, [user, loading]);

  // Save business data with additional error handling and state management
  const saveData = useCallback(async (data) => {      
    if (!user) {      
      console.error("Cannot save business data: No authenticated user");      
      return null;      
    }      
    
    // Prevent multiple simultaneous saves
    if (isSaving) {
      return null;
    }
          
    setIsSaving(true);
    setLoading(true);      
    
    try {      
      // Deep clone the data to avoid reference issues
      const dataToSave = JSON.parse(JSON.stringify(data));
      
      // Ensure all offers have standardized calculations  
      if (dataToSave.offers && dataToSave.offers.length > 0) {  
        dataToSave.offers = dataToSave.offers.map(offer => calculateOfferMetrics({...offer}, dataToSave)); 
      }
      
      const savedData = await saveBusinessData(dataToSave, user.id);      
        
      // Ensure all offers have standardized calculations in the response too
      if (savedData && savedData.offers && savedData.offers.length > 0) {  
        savedData.offers = savedData.offers.map(offer => calculateOfferMetrics({...offer}, savedData));  
      }  
        
      setBusinessData(savedData);      
      setUnsavedChanges(false);      
      setError(null);      
      return savedData;      
    } catch (err) {      
      console.error('Error saving business data:', err);      
      setError('Failed to save business data');      
      return null;      
    } finally {      
      setLoading(false);
      setIsSaving(false);      
    }      
  }, [user, isSaving]);

  // Update business data in state and mark as unsaved      
  const updateBusinessData = useCallback((updatedData) => {      
    // Ensure all offers have standardized calculations  
    if (updatedData && updatedData.offers && updatedData.offers.length > 0) {  
      updatedData.offers = updatedData.offers.map(offer => calculateOfferMetrics({...offer}, updatedData));  
    }  
      
    setBusinessData(updatedData);      
    setUnsavedChanges(true);      
  }, []);

  // Save monthly overhead values to the database
  const saveOverhead = useCallback(async (overheadData) => {
    if (!user) {
      console.error("Cannot save overhead: No authenticated user");
      return false;
    }

    // Prevent multiple simultaneous saves
    if (isSaving) {
      return false;
    }

    setIsSaving(true);

    try {
      const result = await saveMonthlyOverhead(overheadData, user.id);
      
      if (result) {
        // Update local state
        setBusinessData(prevData => ({
          ...prevData,
          monthlyOverhead: overheadData
        }));
        
        setError(null);
        return true;
      } else {
        throw new Error("Failed to save overhead");
      }
    } catch (err) {
      console.error('Error saving overhead:', err);
      setError('Failed to save overhead values');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [user, isSaving]);

  const saveOfferData = useCallback(async (offerData) => {            
    if (!user) {            
      console.error("Cannot save offer: No authenticated user");            
      return null;            
    }      
            
    // Prevent multiple simultaneous saves      
    if (isSaving) {      
      return null;      
    }      
                  
    setIsSaving(true);      
                  
    try {      
      // Deep clone the offer to avoid reference issues      
      const offerToSave = JSON.parse(JSON.stringify(offerData));      
              
      // CRITICAL FIX: Ensure profit margin is explicitly calculated and saved
      const price = Number(offerToSave.price || 0);
      const totalCosts = Number(offerToSave.totalCosts || 0);
      const profit = price - totalCosts;
      
      // Set profit margin explicitly rather than relying on calculateOfferMetrics
      if (price > 0) {
        offerToSave.profit = profit;
        offerToSave.profitMargin = (profit / price) * 100;
      }
              
      // Ensure metrics are calculated with consistent method        
      const calculatedOffer = calculateOfferMetrics(offerToSave, businessData);     
      
      // CRITICAL: Preserve the explicitly calculated profit margin
      if (price > 0) {
        calculatedOffer.profitMargin = (profit / price) * 100;
      }
                  
      await saveOffer(calculatedOffer, user.id);            
                    
      // Create deep copies to ensure state updates properly          
      const deepCopyOffer = JSON.parse(JSON.stringify(calculatedOffer));          
                    
      // Update the local business data state            
      let updatedOffers;            
                    
      if (businessData.offers) {            
        // Try to find and update existing offer            
        const existingIndex = businessData.offers.findIndex(            
          offer => offer.name === calculatedOffer.name            
        );            
                      
        if (existingIndex >= 0) {            
          // Update existing offer with a new array to trigger re-render            
          updatedOffers = [...businessData.offers];            
          updatedOffers[existingIndex] = deepCopyOffer;            
        } else {            
          // Add new offer            
          updatedOffers = [...businessData.offers, deepCopyOffer];            
        }            
      } else {            
        // No offers yet, create new array            
        updatedOffers = [deepCopyOffer];            
      }            
                    
      // Update entire state with new offers array - using functional update to avoid race conditions  
      setBusinessData(prevData => ({            
        ...prevData,            
        offers: updatedOffers            
      }));    
              
      // Return a fresh deep copy to avoid reference issues      
      return deepCopyOffer;            
        
    } catch (error) {            
      console.error('Error saving offer:', error);            
      return null;            
    } finally {      
      setIsSaving(false);      
    }            
  }, [user, isSaving, businessData]);

  // Delete an offer with improved error handling
  const removeOffer = useCallback(async (offerName) => {        
    if (!user) {        
      console.error("Cannot delete offer: No authenticated user");        
      return false;        
    }  
      
    // Prevent operations during data saving  
    if (isSaving) {  
      return false;  
    }  
      
    setIsSaving(true);  
            
    try {        
      const result = await deleteOffer(offerName, user.id);        
      
      if (!result) {
        console.error("Delete operation failed");
        setIsSaving(false);
        return false;
      }
              
      // Update the local business data state        
      const updatedOffers = businessData.offers.filter(offer => offer.name !== offerName);        
      const updatedBusinessData = {        
        ...businessData,        
        offers: updatedOffers        
      };        
              
      setBusinessData(updatedBusinessData);  
      setIsSaving(false);
      return true;  
    } catch (err) {        
      console.error('Error deleting offer:', err);        
      setError('Failed to delete offer');  
      setIsSaving(false);
      return false;  
    }        
  }, [user, isSaving, businessData]);

  const value = useMemo(() => ({      
    businessData,      
    loading,      
    error,      
    unsavedChanges,
    isSaving,
    saveData,      
    updateBusinessData,
    saveOverhead,  // NEW - expose the saveOverhead function     
    saveOfferData,      
    removeOffer,  
    refreshBusinessData  
  }), [
    businessData,
    loading,
    error,
    unsavedChanges,
    isSaving,
    saveData,
    updateBusinessData,
    saveOverhead,  // NEW
    saveOfferData,
    removeOffer,
    refreshBusinessData
  ]);

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;      
});

export const useBusiness = () => useContext(BusinessContext);